import React, { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { ShoppingCart, Package, Store, TrendingUp, Search, Grid, List, Plus, ShoppingBag, AlertCircle, ArrowLeftRight, Star } from 'lucide-react'
import { useCustomerAuth } from '../../contexts/CustomerAuthContext'
import { apiService } from '../../services/apiService'

const KataleHome = () => {
  const { customer } = useCustomerAuth()
  const [featuredProducts, setFeaturedProducts] = useState([])
  const [categories, setCategories] = useState([])
  const [loading, setLoading] = useState(true)
  const [cartCount, setCartCount] = useState(0)
  const [stats, setStats] = useState({ products: 0, sellers: 0, orders: 0, myProducts: 0 })
  const [error, setError] = useState('')
  const [searchQuery, setSearchQuery] = useState('')

  useEffect(() => {
    fetchKataleData()
  }, [customer])

  const fetchKataleData = async () => {
    setLoading(true)
    setError('')
    
    try {
      // Extract MG code from tenant DB name (e.g., kololo_mg from mgfina6_tenant_kololo_mg)
      const getMgCode = (tenantDb) => {
        if (!tenantDb) return null
        // Remove 'mgfina6_tenant_' prefix to get the MG code
        const prefix = 'mgfina6_tenant_'
        if (tenantDb.startsWith(prefix)) {
          return tenantDb.substring(prefix.length)
        }
        // Fallback for old format
        const parts = tenantDb.split('_')
        return parts.length > 1 ? parts[1] : tenantDb
      }
      
      const buyerMemNo = customer?.memberNo
      const buyerMgCode = getMgCode(customer?.tenantDb)
      
      // Fetch categories, products, and cart in parallel
      const [categoriesRes, productsRes, cartRes] = await Promise.all([
        apiService.getKataleCategories(),
        apiService.getKataleProducts({ limit: 6, sort_by: 'newest' }),
        (customer && buyerMemNo && buyerMgCode) ? apiService.getKataleCart(buyerMemNo, buyerMgCode) : Promise.resolve(null)
      ])

      // Handle categories
      if (categoriesRes.status === 'SUCCESS' && categoriesRes.data) {
        const categoryIcons = {
          'Grains & Cereals': '🌾',
          'Vegetables': '🥬',
          'Fruits': '🍎',
          'Legumes': '🫘',
          'Dairy & Eggs': '🥛',
          'Livestock': '🐄',
          'Seeds & Seedlings': '🌱',
          'Farm Equipment': '🚜',
          'Others': '📦',
          'default': '📦'
        }
        setCategories(categoriesRes.data.map(cat => ({
          ...cat,
          icon: categoryIcons[cat.name] || categoryIcons['default'],
          count: cat.product_count || 0
        })))
      }

      // Handle products
      if (productsRes.status === 'SUCCESS' && productsRes.data) {
        setFeaturedProducts(productsRes.data.products || [])
        setStats(prev => ({ 
          ...prev, 
          products: productsRes.data.pagination?.total || productsRes.data.products?.length || 0 
        }))
      }

      // Handle cart
      if (cartRes && cartRes.status === 'SUCCESS' && cartRes.data) {
        setCartCount(cartRes.data.items?.length || 0)
      }

      // Fetch my products count if customer exists
      if (customer && buyerMemNo && buyerMgCode) {
        try {
          const myProductsRes = await apiService.getKataleMyProducts(buyerMemNo, buyerMgCode)
          if (myProductsRes.status === 'SUCCESS' && myProductsRes.data) {
            setStats(prev => ({ ...prev, myProducts: myProductsRes.data.length || 0 }))
          }
        } catch (e) {
          console.log('Could not fetch my products:', e)
        }

        // Fetch orders count
        try {
          const ordersRes = await apiService.getKataleOrders(buyerMemNo, buyerMgCode, 'purchases')
          if (ordersRes.status === 'SUCCESS' && ordersRes.data) {
            setStats(prev => ({ ...prev, orders: ordersRes.data.length || 0 }))
          }
        } catch (e) {
          console.log('Could not fetch orders:', e)
        }
      }

    } catch (err) {
      console.error('Failed to fetch Katale data:', err)
      setError('Failed to load marketplace data. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  const handleSearch = (e) => {
    e.preventDefault()
    if (searchQuery.trim()) {
      window.location.href = `/katale/products?search=${encodeURIComponent(searchQuery)}`
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-UG', {
      style: 'currency',
      currency: 'UGX',
      minimumFractionDigits: 0
    }).format(amount)
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Error Alert */}
      {error && (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
            <AlertCircle className="h-5 w-5 text-red-500 mr-2" />
            <span className="text-red-700">{error}</span>
            <button onClick={fetchKataleData} className="ml-auto text-red-700 hover:text-red-900 underline">
              Retry
            </button>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="flex flex-col md:flex-row items-center justify-between">
            <div className="mb-6 md:mb-0">
              <h1 className="text-4xl font-bold mb-2">
                🌾 MG Katale Marketplace
              </h1>
              <p className="text-blue-100 text-lg">
                Buy and sell agricultural products within your MG community
              </p>
              <p className="text-blue-200 text-sm mt-2">
                Pay directly from your MG savings account balance
              </p>
            </div>
            <div className="flex space-x-4">
              <Link
                to="/katale/cart"
                className="bg-white text-blue-700 px-6 py-3 rounded-lg font-medium hover:bg-blue-50 transition-colors flex items-center"
              >
                <ShoppingCart className="h-5 w-5 mr-2" />
                Cart ({cartCount})
              </Link>
              <Link
                to="/katale/add-product"
                className="bg-blue-500 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-400 transition-colors flex items-center"
              >
                <Plus className="h-5 w-5 mr-2" />
                Sell Product
              </Link>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Quick Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-white rounded-lg shadow-sm p-4 flex items-center">
            <div className="p-3 rounded-full bg-blue-100 mr-4">
              <Package className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Available Products</p>
              <p className="text-xl font-bold text-gray-900">{stats.products}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 flex items-center">
            <div className="p-3 rounded-full bg-blue-100 mr-4">
              <Store className="h-6 w-6 text-blue-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Categories</p>
              <p className="text-xl font-bold text-gray-900">{categories.length}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 flex items-center">
            <div className="p-3 rounded-full bg-yellow-100 mr-4">
              <ShoppingBag className="h-6 w-6 text-yellow-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">My Orders</p>
              <p className="text-xl font-bold text-gray-900">{stats.orders}</p>
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm p-4 flex items-center">
            <div className="p-3 rounded-full bg-purple-100 mr-4">
              <TrendingUp className="h-6 w-6 text-purple-600" />
            </div>
            <div>
              <p className="text-sm text-gray-600">My Products</p>
              <p className="text-xl font-bold text-gray-900">{stats.myProducts}</p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid grid-cols-2 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <Link
            to="/katale/my-products"
            className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-purple-100 mr-3">
                <Store className="h-6 w-6 text-purple-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">My Products</h3>
                <p className="text-xs text-gray-500">Manage your listed products</p>
              </div>
            </div>
          </Link>

          <Link
            to="/katale/orders"
            className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 mr-3">
                <ShoppingBag className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">My Orders</h3>
                <p className="text-xs text-gray-500">Track your purchases</p>
              </div>
            </div>
          </Link>

          <Link
            to="/katale/add-product"
            className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-blue-100 mr-3">
                <Plus className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">Sell a Product</h3>
                <p className="text-xs text-gray-500">List your agricultural products</p>
              </div>
            </div>
          </Link>

          <Link
            to="/katale/ratings"
            className="bg-white rounded-lg shadow-sm p-4 hover:shadow-md transition-shadow"
          >
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-amber-100 mr-3">
                <Star className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 text-sm">Ratings & Reviews</h3>
                <p className="text-xs text-gray-500">Rate sellers & view reviews</p>
              </div>
            </div>
          </Link>
        </div>

        {/* Search Bar */}
        <div className="bg-white rounded-lg shadow-sm p-4 mb-8">
          <form onSubmit={handleSearch} className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search for products... (e.g., maize, beans, tomatoes)"
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
            <Link
              to="/katale/products"
              className="btn btn-primary flex items-center justify-center"
            >
              <Grid className="h-5 w-5 mr-2" />
              Browse All
            </Link>
          </form>
        </div>

        {/* Featured Products */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-xl font-semibold text-gray-900">Featured Products</h2>
            <Link to="/katale/products" className="text-blue-600 hover:text-blue-700 text-sm font-medium">
              View All →
            </Link>
          </div>
          {featuredProducts.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredProducts.map((product) => (
                <div key={product.id} className="bg-white rounded-lg shadow-sm overflow-hidden hover:shadow-md transition-shadow">
                  <div className="h-48 bg-gray-200 flex items-center justify-center">
                    {product.primary_image ? (
                      <img src={product.primary_image} alt={product.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="text-gray-400 text-center">
                        <Package className="h-12 w-12 mx-auto mb-2" />
                        <span className="text-sm">No Image</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <span className="inline-block px-2 py-1 text-xs font-medium bg-blue-100 text-blue-800 rounded-full mb-2">
                      {product.category_name || 'Uncategorized'}
                    </span>
                    <h3 className="font-semibold text-gray-900 mb-1">{product.name}</h3>
                    <p className="text-sm text-gray-600 mb-2 line-clamp-2 min-h-[2.5rem]">
                      {product.description || 'No details provided by seller.'}
                    </p>
                    <p className="text-sm text-gray-500 mb-2">by {product.seller_name || 'Unknown Seller'}</p>
                    <div className="flex items-center justify-between">
                      <span className="text-lg font-bold text-blue-600">{formatCurrency(product.price)}</span>
                      <Link to={`/katale/products/${product.id}`} className="btn btn-sm btn-primary">
                        View Details
                      </Link>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-lg shadow-sm p-8 text-center text-gray-500">
              <Package className="h-12 w-12 mx-auto mb-4 text-gray-300" />
              <p>No products available yet. Be the first to sell!</p>
              <Link to="/katale/add-product" className="btn btn-primary mt-4">
                <Plus className="h-4 w-4 mr-2" />
                Add Product
              </Link>
            </div>
          )}
        </div>

        {/* Info Banner */}
        <div className="mt-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="font-semibold text-blue-800 mb-2">💡 How MG Katale Works</h3>
          <div className="grid md:grid-cols-3 gap-4 text-sm text-blue-700">
            <div>
              <strong>1. Browse & Buy</strong>
              <p>Find agricultural products from fellow MG members at competitive prices.</p>
            </div>
            <div>
              <strong>2. Pay with Savings</strong>
              <p>Use your savings balance to pay securely within the platform. 5% commission applies.</p>
            </div>
            <div>
              <strong>3. Sell & Earn</strong>
              <p>List your own products and receive payments directly to your MG account.</p>
            </div>
          </div>
        </div>

        {/* Buyer Protection T&Cs */}
        <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-6">
          <h3 className="font-semibold text-green-800 mb-2">🛡️ Buyer Protection & Escrow</h3>
          <div className="grid md:grid-cols-2 gap-4 text-sm text-green-700">
            <div>
              <strong>Escrow Protection</strong>
              <p>Payment is held securely by UFCC and only released to the seller after you confirm receipt and quality of goods.</p>
            </div>
            <div>
              <strong>Terms & Conditions</strong>
              <p>If products delivered are of poor quality and are refused by the buyer, the seller bears the full burden of transporting the goods back. Disputes are mediated by UFCC.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default KataleHome
